/**
 * LiveActivityStrip — what Rammy is reading, while she's still writing.
 *
 * The backend emits a single `progress` event before the first token with
 * the tools that fired and the real source titles behind them. Instead of a
 * generic "thinking…", the wait shows the actual provenance arriving one chip
 * at a time; when the reply finishes, the chips hand over to the SourceBadge
 * pill under the message.
 *
 * Props:
 *   label    — "Checked the weather · composing your reply…"
 *   sources  — ["Open-Meteo · Bengaluru, IN", "Wikipedia · Bengaluru"]
 *   testid
 */
import React from "react";
import * as L from "lucide-react";

// Strip the "Provider · Detail" tail so a chip stays a chip.
const shortName = (title) => {
  const head = String(title).split("·")[0].trim();
  return head.length > 22 ? `${head.slice(0, 21)}…` : head;
};

export const LiveActivityStrip = ({ label, sources = [], testid }) => {
  const chips = sources.slice(0, 4);
  if (!label && !chips.length) return null;

  return (
    <div
      className="mb-2 flex flex-wrap items-center gap-1.5 fade-up"
      data-testid={testid}
      aria-live="polite"
    >
      <span className="relative inline-flex items-center gap-1.5 text-[11px] font-medium text-[var(--rammy-cyan-dark)]">
        <span className="relative flex h-1.5 w-1.5">
          <span className="absolute inline-flex h-full w-full rounded-full bg-[var(--rammy-cyan-dark)] opacity-60 animate-ping" />
          <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-[var(--rammy-cyan-dark)]" />
        </span>
        {chips.length
          ? "Reading"
          : (label ? label.replace(" · composing your reply…", "") : "Reading sources")}
      </span>

      {chips.map((s, k) => (
        <span
          key={`${s}-${k}`}
          title={s}
          style={{ animationDelay: `${120 + k * 110}ms` }}
          className="fade-up inline-flex items-center gap-1 rounded-full border border-[var(--rammy-cyan)]/35
                     bg-[var(--rammy-cyan)]/[0.07] px-2 py-0.5 text-[10.5px] text-[var(--rammy-text-sec)]
                     transition-colors duration-300 hover:bg-[var(--rammy-cyan)]/15"
          data-testid={`${testid}-chip-${k}`}
        >
          <L.Radio size={9} className="text-[var(--rammy-cyan-dark)] shrink-0" />
          {shortName(s)}
        </span>
      ))}
    </div>
  );
};

export default LiveActivityStrip;
